import type { SchoolComparisonPeerGroupView } from '@/features/reports/api/schoolClassificationComparison.dto';

/** One peer band with cost + tuition fields for absolute-only charts. */
export interface PeerGroupMetrics {
  label: string;
  schoolCount: number;
  costPerStudentUSD: number;
  avgCostPerStudentUSD: number;
  medianCostPerStudentUSD: number;
  minCostPerStudentUSD: number;
  maxCostPerStudentUSD: number;
  displayMinCostPerStudentUSD: number;
  displayMaxCostPerStudentUSD: number;
  tuitionPerStudentUSD: number;
  avgTuitionPerStudentUSD: number;
  medianTuitionPerStudentUSD: number;
  minTuitionPerStudentUSD: number;
  maxTuitionPerStudentUSD: number;
  displayMinTuitionPerStudentUSD: number;
  displayMaxTuitionPerStudentUSD: number;
  bandTuitionMin?: number;
  bandTuitionMax?: number;
  peerMinInBandTuition?: number;
  peerMaxInBandTuition?: number;
  closestBelowSchoolTuition?: number;
  closestAboveSchoolTuition?: number;
  budgetId?: string;
  minBudgetId?: string;
  maxBudgetId?: string;
  minBudgetSchoolName?: string;
  maxBudgetSchoolName?: string;
  /** From API `costRank` on the peer group node. */
  costRank?: number;
  /** From API `tuitionRank` on the peer group node. */
  tuitionRank?: number;
}

function inferDisplaySpan(
  actual: number,
  avg: number,
  median: number,
  displayMin?: number,
  displayMax?: number,
  rawMin?: number,
  rawMax?: number
): { displayMin: number; displayMax: number } {
  if (Number.isFinite(displayMin) && Number.isFinite(displayMax) && displayMax! > displayMin!) {
    return { displayMin: displayMin!, displayMax: displayMax! };
  }
  if (Number.isFinite(rawMin) && Number.isFinite(rawMax) && rawMax! > rawMin!) {
    return { displayMin: rawMin!, displayMax: rawMax! };
  }
  const vals = [actual, avg, median].filter((x) => Number.isFinite(x));
  if (vals.length === 0) {
    const pad = 1;
    return { displayMin: actual - pad, displayMax: actual + pad };
  }
  const lo = Math.min(...vals);
  const hi = Math.max(...vals);
  if (hi <= lo) {
    const pad = Math.max(Math.abs(lo) * 0.05, 1);
    return { displayMin: lo - pad, displayMax: lo + pad };
  }
  const pad = (hi - lo) * 0.08;
  return { displayMin: lo - pad, displayMax: hi + pad };
}

export function buildPeerGroupMetrics(
  group: SchoolComparisonPeerGroupView,
  schoolTuitionFallback: number,
  schoolCostFallback: number
): PeerGroupMetrics {
  const tuitionPerStudentUSD = group.tuitionPerStudentUSD ?? group.tuition?.realValue ?? schoolTuitionFallback;
  const avgTuitionPerStudentUSD = Number.isFinite(group.avgTuitionPerStudentUSD) ? group.avgTuitionPerStudentUSD : 0;
  const medianTuitionPerStudentUSD =
    (group.medianTuitionPerStudentUSD != null && Number.isFinite(group.medianTuitionPerStudentUSD)
      ? group.medianTuitionPerStudentUSD
      : group.tuition?.median) ?? avgTuitionPerStudentUSD;
  const tAct = Number.isFinite(tuitionPerStudentUSD) ? tuitionPerStudentUSD : schoolTuitionFallback;
  const tMed = Number.isFinite(medianTuitionPerStudentUSD) ? medianTuitionPerStudentUSD : avgTuitionPerStudentUSD;
  const tSpan = inferDisplaySpan(
    tAct,
    avgTuitionPerStudentUSD,
    tMed,
    group.displayMinTuitionPerStudentUSD,
    group.displayMaxTuitionPerStudentUSD,
    group.minTuitionPerStudentUSD,
    group.maxTuitionPerStudentUSD
  );

  const costPerStudentUSD = group.costPerStudentUSD ?? group.costPerStudent?.realValue ?? schoolCostFallback;
  const avgCostPerStudentUSD = Number.isFinite(group.avgCostPerStudentUSD) ? group.avgCostPerStudentUSD : 0;
  const medianCostPerStudentUSD =
    (group.medianCostPerStudentUSD != null && Number.isFinite(group.medianCostPerStudentUSD)
      ? group.medianCostPerStudentUSD
      : group.costPerStudent?.median) ?? avgCostPerStudentUSD;
  const cAct = Number.isFinite(costPerStudentUSD) ? costPerStudentUSD : schoolCostFallback;
  const cMed = Number.isFinite(medianCostPerStudentUSD) ? medianCostPerStudentUSD : avgCostPerStudentUSD;
  const cSpan = inferDisplaySpan(
    cAct,
    avgCostPerStudentUSD,
    cMed,
    group.displayMinCostPerStudentUSD,
    group.displayMaxCostPerStudentUSD,
    group.minCostPerStudentUSD,
    group.maxCostPerStudentUSD
  );

  return {
    label: group.label,
    schoolCount: group.schoolCount,
    costPerStudentUSD: cAct,
    avgCostPerStudentUSD: avgCostPerStudentUSD,
    medianCostPerStudentUSD: cMed,
    minCostPerStudentUSD:
      group.minCostPerStudentUSD != null && Number.isFinite(group.minCostPerStudentUSD)
        ? group.minCostPerStudentUSD
        : cSpan.displayMin,
    maxCostPerStudentUSD:
      group.maxCostPerStudentUSD != null && Number.isFinite(group.maxCostPerStudentUSD)
        ? group.maxCostPerStudentUSD
        : cSpan.displayMax,
    displayMinCostPerStudentUSD: cSpan.displayMin,
    displayMaxCostPerStudentUSD: cSpan.displayMax,
    tuitionPerStudentUSD: tAct,
    avgTuitionPerStudentUSD,
    medianTuitionPerStudentUSD: tMed,
    minTuitionPerStudentUSD:
      group.minTuitionPerStudentUSD != null && Number.isFinite(group.minTuitionPerStudentUSD)
        ? group.minTuitionPerStudentUSD
        : tSpan.displayMin,
    maxTuitionPerStudentUSD:
      group.maxTuitionPerStudentUSD != null && Number.isFinite(group.maxTuitionPerStudentUSD)
        ? group.maxTuitionPerStudentUSD
        : tSpan.displayMax,
    displayMinTuitionPerStudentUSD: tSpan.displayMin,
    displayMaxTuitionPerStudentUSD: tSpan.displayMax,
    bandTuitionMin: group.bandTuitionMin,
    bandTuitionMax: group.bandTuitionMax,
    peerMinInBandTuition: group.peerMinInBandTuition,
    peerMaxInBandTuition: group.peerMaxInBandTuition,
    closestBelowSchoolTuition: group.closestBelowSchoolTuition,
    closestAboveSchoolTuition: group.closestAboveSchoolTuition,
    budgetId: group.budgetId,
    minBudgetId: group.minBudgetId,
    maxBudgetId: group.maxBudgetId,
    minBudgetSchoolName: group.minBudgetSchoolName,
    maxBudgetSchoolName: group.maxBudgetSchoolName,
    costRank: group.costRank,
    tuitionRank: group.tuitionRank,
  };
}
